import { apiFetch } from './client.js'

function normalizeGroupConfig(groupConfig) {
  if (!groupConfig || typeof groupConfig !== 'object') {
    return null
  }

  const normalizedStatus = String(groupConfig.status || '').trim()
  const availableStatuses = Array.isArray(groupConfig.available_statuses)
    ? groupConfig.available_statuses
    : Array.isArray(groupConfig.availableStatuses)
      ? groupConfig.availableStatuses
      : []

  return {
    id: groupConfig.id,
    applicationId: groupConfig.application_id || groupConfig.applicationId,
    name: groupConfig.name || '',
    description: groupConfig.description || '',
    status: normalizedStatus || 'Draft',
    availableStatuses,
    startBundleVersion: groupConfig.start_bundle_version || groupConfig.startBundleVersion || '',
    draftRemoteConfigId: groupConfig.draft_remote_config_id || groupConfig.draftRemoteConfigId || null,
    defaultSegmentationId: groupConfig.default_segmentation_id || groupConfig.defaultSegmentationId || null,
    defaultGroupSegmentId: groupConfig.default_group_segment_id || groupConfig.defaultGroupSegmentId || null,
    createdAt: groupConfig.created_at || groupConfig.createdAt || null,
    updatedAt: groupConfig.updated_at || groupConfig.updatedAt || null,
  }
}

function normalizeGroupConfigVersion(version) {
  if (!version || typeof version !== 'object') return null
  return {
    id: version.id,
    applicationId: version.application_id || version.applicationId,
    groupConfigId: version.group_config_id || version.groupConfigId,
    parentId: version.parent_id || version.parentId || null,
    bundleVersion: version.bundle_version || version.bundleVersion || '',
    status: String(version.status || 'Draft').trim(),
    availableStatuses: Array.isArray(version.available_statuses)
      ? version.available_statuses
      : Array.isArray(version.availableStatuses)
        ? version.availableStatuses
        : [],
    createdAt: version.created_at || version.createdAt || null,
    updatedAt: version.updated_at || version.updatedAt || null,
  }
}

function normalizeGroupSegment(groupSegment) {
  if (!groupSegment || typeof groupSegment !== 'object') {
    return null
  }

  const segmentationId = groupSegment.segmentation_id ?? groupSegment.segmentationId ?? null
  return {
    id: groupSegment.id,
    applicationId: groupSegment.application_id || groupSegment.applicationId,
    groupConfigId: groupSegment.group_config_id ?? groupSegment.groupConfigId ?? null,
    remoteConfigId: groupSegment.remote_config_id || groupSegment.remoteConfigId,
    segmentationId: segmentationId ? String(segmentationId) : null,
    segmentKey: segmentationId ? String(segmentationId) : (groupSegment.segment_key || groupSegment.segmentKey || ''),
    priority: Number(groupSegment.priority || 0),
    isEnabled: Boolean(groupSegment.is_enabled ?? groupSegment.isEnabled),
    isDefault: Boolean(groupSegment.is_default ?? groupSegment.isDefault),
    createdAt: groupSegment.created_at || groupSegment.createdAt || null,
    updatedAt: groupSegment.updated_at || groupSegment.updatedAt || null,
  }
}

function normalizeElementBinding(binding) {
  if (!binding || typeof binding !== 'object') {
    return null
  }

  return {
    id: binding.id,
    applicationId: binding.application_id || binding.applicationId,
    groupSegmentId: binding.group_segment_id || binding.groupSegmentId,
    elementId: Number(binding.element_id ?? binding.elementId),
    conditionExpression: binding.condition_expression || binding.conditionExpression || '',
    order: Number(binding.order || 0),
    createdAt: binding.created_at || binding.createdAt || null,
    updatedAt: binding.updated_at || binding.updatedAt || null,
  }
}

function normalizeDynamicRule(rule) {
  if (!rule || typeof rule !== 'object') {
    return null
  }

  return {
    id: rule.id,
    applicationId: rule.application_id || rule.applicationId,
    groupConfigId: rule.group_config_id || rule.groupConfigId || null,
    remoteConfigId: rule.remote_config_id || rule.remoteConfigId || null,
    name: rule.name || '',
    ruleType: rule.rule_type || rule.ruleType || 'Value',
    description: rule.description || '',
    placeholder: rule.placeholder || '',
    outputLabel: rule.output_label || rule.outputLabel || '',
    userParam: rule.user_param || rule.userParam || '',
    dynamicRuleElements: Array.isArray(rule.dynamic_rule_elements)
      ? rule.dynamic_rule_elements
      : Array.isArray(rule.dynamicRuleElements)
        ? rule.dynamicRuleElements
        : [],
    isEnabled: Boolean(rule.is_enabled ?? rule.isEnabled),
    createdAt: rule.created_at || rule.createdAt || null,
    updatedAt: rule.updated_at || rule.updatedAt || null,
  }
}

function extractList(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.items)) return payload.items
  if (Array.isArray(payload?.results)) return payload.results
  if (Array.isArray(payload?.data)) return payload.data
  return []
}

function extractItem(payload) {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    if (payload.item) return payload.item
    if (payload.data && !Array.isArray(payload.data)) return payload.data
  }
  return payload
}

export async function listGroupConfigs(applicationId) {
  const payload = await apiFetch(`/apps/${encodeURIComponent(applicationId)}/group-configs`)
  return extractList(payload).map(normalizeGroupConfig).filter(Boolean)
}

export async function createGroupConfig(applicationId, values) {
  const body = {
    name: String(values?.name || '').trim(),
    description: String(values?.description || '').trim(),
    start_bundle_version: String(values?.startBundleVersion || '').trim(),
    default_element_name: String(values?.defaultElementName || '').trim(),
    default_element_type: String(values?.defaultElementType || 'advertising').trim(),
  }
  const rawJson = String(values?.defaultElementJsonConfig ?? '').trim()
  if (rawJson) body.default_element_raw_json_config = rawJson
  const desc = String(values?.defaultElementDescription ?? '').trim()
  if (desc) body.default_element_description = desc
  const text = String(values?.defaultElementText ?? '').trim()
  if (text) body.default_element_text = text

  const payload = await apiFetch(`/apps/${encodeURIComponent(applicationId)}/group-configs`, {
    method: 'POST',
    body,
  })
  return normalizeGroupConfig(extractItem(payload))
}

export async function setGroupConfigStatus(applicationId, groupConfigId, status) {
  const normalizedStatus = String(status || '').trim()
  const path = `/apps/${encodeURIComponent(applicationId)}/group-configs/${encodeURIComponent(groupConfigId)}`
  const body = {
    status: normalizedStatus,
  }
  const payload = await apiFetch(`${path}/status`, {
    method: 'POST',
    body,
  })
  return normalizeGroupConfig(extractItem(payload))
}

export async function listGroupConfigVersions(applicationId, groupConfigId) {
  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/group-configs/${encodeURIComponent(groupConfigId)}/configs`,
  )
  return extractList(payload).map(normalizeGroupConfigVersion).filter(Boolean)
}

export async function setGroupConfigVersionStatus(applicationId, groupConfigId, remoteConfigId, status) {
  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/group-configs/${encodeURIComponent(groupConfigId)}/status`,
    {
      method: 'POST',
      body: {
        status: String(status || '').trim(),
        remote_config_id: remoteConfigId,
      },
    },
  )
  const item = extractItem(payload)
  return {
    remoteConfigId: item?.remote_config_id || remoteConfigId,
    status: String(item?.status || status || '').trim(),
    availableStatuses: Array.isArray(item?.available_statuses) ? item.available_statuses : [],
  }
}

export async function copyGroupConfigVersion(applicationId, groupConfigId, remoteConfigId) {
  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/group-configs/${encodeURIComponent(
      groupConfigId,
    )}/configs/${encodeURIComponent(remoteConfigId)}/copy`,
    {
      method: 'POST',
    },
  )
  return normalizeGroupConfigVersion(extractItem(payload))
}

export async function createGroupConfigVersion(applicationId, groupConfigId) {
  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/group-configs/${encodeURIComponent(groupConfigId)}/configs/create`,
    {
      method: 'POST',
    },
  )
  return normalizeGroupConfigVersion(extractItem(payload))
}

export async function deleteGroupConfigVersion(applicationId, groupConfigId, remoteConfigId) {
  await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/group-configs/${encodeURIComponent(
      groupConfigId,
    )}/configs/${encodeURIComponent(remoteConfigId)}`,
    {
      method: 'DELETE',
    },
  )
}

export async function addExistingSegmentationToGroupConfig(applicationId, groupConfigId, segmentationId) {
  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/group-configs/${encodeURIComponent(groupConfigId)}/segments`,
    {
      method: 'POST',
      body: {
        segmentation_id: segmentationId,
      },
    },
  )
  return normalizeGroupSegment(extractItem(payload))
}

export async function listGroupConfigSegments(applicationId, groupConfigId, options = {}) {
  let path = `/apps/${encodeURIComponent(applicationId)}/group-configs/${encodeURIComponent(groupConfigId)}/segments`
  const remoteConfigId = options?.remoteConfigId
  if (remoteConfigId) {
    path += `?remote_config_id=${encodeURIComponent(remoteConfigId)}`
  }
  const payload = await apiFetch(path)
  return extractList(payload).map(normalizeGroupSegment).filter(Boolean)
}

export async function listGroupConfigElementBindings(applicationId, groupConfigId, options = {}) {
  let path = `/apps/${encodeURIComponent(applicationId)}/group-configs/${encodeURIComponent(groupConfigId)}/element-bindings`
  const remoteConfigId = options?.remoteConfigId
  if (remoteConfigId) {
    path += `?remote_config_id=${encodeURIComponent(remoteConfigId)}`
  }
  const payload = await apiFetch(path)
  return extractList(payload).map(normalizeElementBinding).filter(Boolean)
}

export async function addElementToGroupSegment(
  applicationId,
  groupConfigId,
  groupSegmentId,
  elementId,
  conditionExpression = '',
) {
  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/group-configs/${encodeURIComponent(groupConfigId)}/element-bindings`,
    {
      method: 'POST',
      body: {
        group_segment_id: groupSegmentId,
        element_id: Number(elementId),
        condition_expression: String(conditionExpression || '').trim(),
      },
    },
  )
  return normalizeElementBinding(extractItem(payload))
}

export async function updateGroupConfigElementBinding(
  applicationId,
  groupConfigId,
  bindingId,
  values = {},
) {
  const payload = await apiFetch(
    `/apps/${encodeURIComponent(applicationId)}/group-configs/${encodeURIComponent(
      groupConfigId,
    )}/element-bindings/${encodeURIComponent(bindingId)}`,
    {
      method: 'PATCH',
      body: {
        ...(values?.elementId !== undefined ? { element_id: Number(values.elementId) } : {}),
        ...(values?.conditionExpression !== undefined
          ? { condition_expression: String(values.conditionExpression || '').trim() }
          : {}),
      },
    },
  )
  return normalizeElementBinding(extractItem(payload))
}

export async function listGroupConfigDynamicRules(applicationId, groupConfigId) {
  const payload = await apiFetch(`/apps/${encodeURIComponent(applicationId)}/dynamic-rules`)
  const rules = extractList(payload).map(normalizeDynamicRule).filter(Boolean)
  if (!groupConfigId) {
    return rules
  }
  return rules.filter((rule) => {
    if (rule.groupConfigId == null) return true
    return String(rule.groupConfigId) === String(groupConfigId)
  })
}

export async function createGroupConfigDynamicRule(applicationId, groupConfigId, values) {
  const payload = await apiFetch(`/apps/${encodeURIComponent(applicationId)}/dynamic-rules`, {
    method: 'POST',
    body: {
      group_config_id: groupConfigId == null ? null : Number(groupConfigId),
      name: String(values?.name || '').trim(),
      rule_type: String(values?.ruleType || 'Value').trim(),
      description: String(values?.description || '').trim(),
      placeholder: String(values?.placeholder || '').trim(),
      output_label: String(values?.outputLabel || '').trim(),
      user_param: String(values?.userParam || '').trim(),
      dynamic_rule_elements: Array.isArray(values?.dynamicRuleElements)
        ? values.dynamicRuleElements
        : [],
      is_enabled: Boolean(values?.isEnabled ?? true),
    },
  })
  return normalizeDynamicRule(extractItem(payload))
}
